import type { SectionProps } from "../components/Questionnaire/Section";
import type {
  Answer,
  QuestionProps,
} from "../components/Questionnaire/Section/Question/types";
import type { Answers } from "../features/questionnaire";
import type { Locale } from "../types";
import { transformBracketsForRails } from "./transformBracketsForRails";
import { formatDateByLocale } from "./transformDates";

/**
 * Creates a metadata map for questions where:
 * - key is question name
 * - value is object containing question id and display type
 */
const createQuestionMetadataMap = (
  sections: SectionProps[],
): Map<string, Record<string, string>> => {
  const questionMap = new Map<string, Record<string, string>>();

  return sections.reduce((map, section) => {
    (section.questions as QuestionProps[]).forEach(({ name, id, display }) =>
      map.set(name, { id, type: display?.type }),
    );
    return map;
  }, questionMap);
};

/**
 * Determines if a question should be excluded from the final answers payload
 */
const shouldExclude = (
  q: QuestionProps,
  visibility: Record<string, boolean>,
): boolean => {
  return (
    q.includeAnswer === false ||
    (typeof q.includeAnswer === "undefined" && !visibility[q.name]) ||
    q.display?.type === "percentage_split"
  );
};

const removeHiddenAnswers = (
  answers: Answers,
  sections: SectionProps[],
  visibility: Record<string, boolean>,
): Answers => {
  const answersToExclude = new Set(
    sections
      .flatMap(section => section.questions as QuestionProps[])
      .filter(q => shouldExclude(q, visibility))
      .map(q => q.name),
  );

  return Object.fromEntries(
    Object.entries(answers).filter(([key]) => !answersToExclude.has(key)),
  );
};

const transformAnswerValue = (
  value: Answer,
  type?: string,
  locale?: Locale,
): Answer => {
  if (type === "date" && typeof value === "string") {
    return formatDateByLocale(value, locale);
  }

  // Trim string values before sending to server (but preserve arrays)
  if (typeof value === "string") {
    return value.trim();
  }

  return value;
};

/**
 * Transforms address lookup answers to match the expected API format and
 * removes default address lookup question values
 * @param answers Original answers object
 * @param sections All form sections
 * @returns Transformed answers with address fields in format questionId[fieldId]
 */
const transformAddressLookupAnswers = (
  answers: Answers,
  sections: SectionProps[],
): Answers => {
  // Collect the questions to exclude and field mappings
  const { addressQuestions, fieldMappings } = sections.reduce<{
    addressQuestions: Set<string>;
    fieldMappings: Record<string, string>;
  }>(
    (acc, section) => {
      // Address lookup always contains Question type
      (section.questions as QuestionProps[])
        .filter(q => q.display?.type === "address_lookup")
        .forEach(question => {
          // Add the question name to be excluded
          acc.addressQuestions.add(question.name);

          // Process mapped fields
          const mappedFields = question.display.config?.mappedFields as {
            id: string;
            associatedQuestionId: string;
          }[];
          if (mappedFields && Array.isArray(mappedFields)) {
            mappedFields.forEach(field => {
              const fullKey = `${question.id}[${field.id}]`;
              if (field.associatedQuestionId) {
                acc.fieldMappings[field.associatedQuestionId] = fullKey;
              }
            });
          }
        });
      return acc;
    },
    { addressQuestions: new Set<string>(), fieldMappings: {} },
  );

  // Create new answers object with transformed address fields
  const transformed = Object.entries(answers).reduce<Record<string, Answer>>(
    (acc, [key, value]) => {
      // Skip address lookup questions
      if (addressQuestions.has(key)) {
        return acc;
      }

      // Transform field IDs to full keys and keep original
      acc[key] = value;
      if (fieldMappings[key]) {
        acc[fieldMappings[key]] = value;
      }
      return acc;
    },
    {},
  );

  return transformed;
};

// Generates an answers shape for requests
export const transformAnswers = (
  answers: Answers,
  sections: SectionProps[],
  visibility: Record<string, boolean> = {},
  locale?: Locale,
): Answers => {
  const withTransformedAddressLookupAnswers = transformAddressLookupAnswers(
    answers,
    sections,
  );
  const questionMetadata = createQuestionMetadataMap(sections);
  const filteredAnswers = removeHiddenAnswers(
    withTransformedAddressLookupAnswers,
    sections,
    visibility,
  );

  return Object.entries(filteredAnswers).reduce<Record<string, Answer>>(
    (acc, [key, value]) => {
      const questionId = questionMetadata.get(key)?.id || key;
      const newKey = transformBracketsForRails(questionId);
      acc[newKey] = transformAnswerValue(
        value,
        questionMetadata.get(key)?.type,
        locale,
      );
      return acc;
    },
    {},
  );
};
